
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { getCurrentUser, logout, checkSession } from '../api/authService';
import { useNavigate } from 'react-router-dom';
import { mergeAuthenticatedUser, resolveAuthToken } from './authUtils';

export const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [userState, setUserState] = useState(null);
  const [tokenState, setTokenState] = useState(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const navigate = useNavigate();

  const readStoredAuth = useCallback(() => {
    try {
      return getCurrentUser();
    } catch (error) {
      console.warn('[AuthContext] Failed to read stored auth payload:', error);
      return null;
    }
  }, []);

  const enrichUser = useCallback((rawUser) => {
    if (!rawUser || typeof rawUser !== 'object') return rawUser;

    const rawStatus = rawUser.subscription?.status;
    const statusNormalized = typeof rawStatus === 'string' ? rawStatus.toLowerCase() : undefined;
    let normalized = statusNormalized || 'free-tier';
    if (normalized === 'pro' || normalized === 'active') {
      normalized = 'ultra';
    }
    if (!['free-tier', 'ultra'].includes(normalized)) {
      if (rawStatus !== undefined) {
        console.warn('[Subscription] Unknown subscription status; coercing to free-tier', {
          status: rawStatus,
          userId: rawUser?._id,
          email: rawUser?.email
        });
      }
      normalized = 'free-tier';
    }

    if (rawUser.subscriptionPlan === normalized) {
      return rawUser;
    }
    return { ...rawUser, subscriptionPlan: normalized };
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setUserState(null);
    setTokenState(null);
    if (!window.location.pathname.startsWith('/admin/')) {
      navigate('/login');
    }
  }, [navigate]);

  const checkUserSession = useCallback(async () => {
    const stored = readStoredAuth();
    if (!stored?.token) {
      setTokenState(null);
      setLoading(false);
      return;
    }
    setTokenState(stored.token);

    try {
      const data = await checkSession();
      if (data && data.user) {
        setUserState(enrichUser(mergeAuthenticatedUser(stored.user, data.user)));
        setTokenState(resolveAuthToken({ sessionData: data, storedToken: stored.token }));
      } else {
        handleLogout();
      }
    } catch (error) {
      console.error('Session check failed:', error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  }, [handleLogout, enrichUser, readStoredAuth]);

  useEffect(() => {
    checkUserSession();
  }, [checkUserSession]);

  const updateUser = (newUserObject) => {
    const currentUser = readStoredAuth(); // this gets { token, user }
    if (currentUser) {
      const mergedUser = enrichUser(mergeAuthenticatedUser(currentUser.user, newUserObject));
      const updatedData = { ...currentUser, user: mergedUser };
      localStorage.setItem('user', JSON.stringify(updatedData));
      setUserState(mergedUser);
      setTokenState(updatedData.token || null);
    }
  };

  const setUser = useCallback((nextUserOrUpdater) => {
    setUserState((prevUser) => {
      const nextUser = typeof nextUserOrUpdater === 'function'
        ? nextUserOrUpdater(prevUser)
        : nextUserOrUpdater;
      return enrichUser(mergeAuthenticatedUser(prevUser, nextUser));
    });
    const stored = readStoredAuth();
    setTokenState(stored?.token || null);
  }, [enrichUser, readStoredAuth]);

  return (
    <AuthContext.Provider value={{ user: userState, token: tokenState, setUser, updateUser, logout: handleLogout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
